import { useMemo, useState } from 'react';
import {
  alertKind,
  compassWord,
  etaToPoint,
  formatDistance,
  formatEtaRange,
  KIND_COLORS,
  type Asset,
  type EtaResult,
  type TenantAlert,
} from '@ww/shared';
import {
  canUseGeolocation,
  clearHome,
  getHome,
  homeFromAsset,
  homeFromTenant,
  nearestAsset,
  requestGeolocation,
  setHome,
  type HomePoint,
} from '../lib/home';
import { hazardSentence, humanEvent } from '../lib/text';
import { useNowcast } from '../hooks/useNowcast';
import { fmtTime } from '../lib/time';

interface Props {
  /** Rendered as a floating panel over the map; the button lives in the page header. */
  onClose: () => void;
  tenantId: string | null | undefined;
  tenantCenter?: [number, number] | null;
  tenantLabel: string;
  timezone?: string | null;
  alerts: TenantAlert[];
  assets: Asset[];
}

interface Candidate {
  ta: TenantAlert;
  eta: EtaResult;
}

/** How bad is it, for ordering: something arriving beats something that might. */
function rank(c: Candidate): number {
  if (c.eta.status === 'inside') return -1;
  if (c.eta.status === 'arriving') return c.eta.minutesLow ?? c.eta.minutes ?? 9_000;
  if (c.eta.status === 'unknown') return 10_000 + c.eta.distanceMiles;
  return 20_000 + c.eta.distanceMiles; // moving away
}

export default function WhenHitsCard({
  onClose,
  tenantId,
  tenantCenter,
  tenantLabel,
  timezone,
  alerts,
  assets,
}: Props) {
  const [home, setHomeState] = useState<HomePoint | null>(() => getHome(tenantId));
  // Open straight into the chooser when no home point has been picked yet.
  const [choosing, setChoosing] = useState(() => !getHome(tenantId));
  const [locating, setLocating] = useState(false);
  const [locError, setLocError] = useState<string | null>(null);

  const effectiveHome = home ?? homeFromTenant(tenantCenter, tenantLabel);

  const candidates = useMemo<Candidate[]>(() => {
    if (!effectiveHome) return [];
    const now = new Date().toISOString();
    const point: [number, number] = [effectiveHome.lon, effectiveHome.lat];
    return alerts
      .filter((ta) => ta.alert?.geometry)
      .map((ta) => ({ ta, eta: etaToPoint(ta.alert.geometry, ta.alert.motion, point, now) }))
      .sort((a, b) => rank(a) - rank(b));
  }, [alerts, effectiveHome]);

  // Rain that carries no warning: radar-derived motion + footprint from the server.
  const nowcast = useNowcast(effectiveHome ? [effectiveHome.lon, effectiveHome.lat] : null);
  const rain = useMemo<EtaResult | null>(() => {
    if (!effectiveHome || !nowcast?.geometry || !nowcast.motion) return null;
    return etaToPoint(
      nowcast.geometry,
      nowcast.motion,
      [effectiveHome.lon, effectiveHome.lat],
      new Date().toISOString(),
    );
  }, [nowcast, effectiveHome]);

  const warningTop = candidates[0] ?? null;
  // A warning always outranks radar rain: it is the forecaster's own vector and it is
  // the thing that matters. Rain only speaks when the warning path has nothing to say.
  const warningAnswers =
    warningTop && (warningTop.eta.status === 'inside' || warningTop.eta.status === 'arriving');
  const showRain = !warningAnswers && (rain?.status === 'arriving' || rain?.status === 'inside');
  const top = warningTop;

  function commit(p: HomePoint) {
    setHomeState(p);
    setHome(tenantId, p);
    setChoosing(false);
    setLocError(null);
  }

  async function useMyLocation() {
    setLocating(true);
    setLocError(null);
    try {
      commit(await requestGeolocation());
    } catch (e) {
      setLocError((e as Error).message);
    } finally {
      setLocating(false);
    }
  }

  return (
    <div
      className="panel"
      style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 10, width: '100%', marginTop: 10 }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        <div style={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {choosing || !effectiveHome ? (
            <HomeChooser
              assets={assets}
              tenantCenter={tenantCenter}
              tenantLabel={tenantLabel}
              locating={locating}
              locError={locError}
              onUseLocation={useMyLocation}
              onPickAsset={(a) => commit(homeFromAsset(a))}
              onCancel={effectiveHome ? () => setChoosing(false) : undefined}
            />
          ) : (
            <>
              {showRain && rain ? (
                <PrecipAnswer eta={rain} home={effectiveHome} />
              ) : (
                <Answer top={top} home={effectiveHome} timezone={timezone} tenantLabel={tenantLabel} />
              )}
              {candidates.length > 1 && (
                <div style={{ fontSize: 12.5, color: '#93a5c1' }}>
                  {candidates.length - 1} other active {candidates.length - 1 === 1 ? 'alert' : 'alerts'} in the
                  area.
                </div>
              )}
              <Hedge source={showRain ? 'radar' : top?.ta.alert.motion?.source ?? null} />
              <div style={{ fontSize: 12.5, color: '#93a5c1', borderTop: '1px solid #1e2c46', paddingTop: 8 }}>
                Location: <strong style={{ color: '#c9d7ee' }}>{effectiveHome.label}</strong>
                {effectiveHome.source === 'tenant' && ' (county centre — not your actual position)'}{' '}
                <button
                  type="button"
                  className="chip hov"
                  style={{ marginLeft: 6 }}
                  onClick={() => {
                    clearHome(tenantId);
                    setHomeState(null);
                    setChoosing(true);
                  }}
                >
                  Change
                </button>
              </div>
            </>
          )}
        </div>
        <button
          type="button"
          className="chip hov"
          onClick={onClose}
          aria-label="Close arrival estimate"
          style={{ flexShrink: 0, width: 28, height: 28, padding: 0, borderRadius: 14 }}
        >
          ✕
        </button>
      </div>
    </div>
  );
}

function Answer({
  top,
  home,
  timezone,
  tenantLabel,
}: {
  top: Candidate | null;
  home: HomePoint;
  timezone?: string | null;
  tenantLabel: string;
}) {
  if (!top) {
    return (
      <Headline tone="#78b7ff" text={`No active warnings near ${home.label}.`}>
        <span>Watching {tenantLabel}.</span>
      </Headline>
    );
  }

  const { ta, eta } = top;
  const event = humanEvent(ta.alert.event);
  const color = KIND_COLORS[alertKind(ta.alert.event)]?.fill ?? '#78b7ff';
  const motion = ta.alert.motion;
  const hazards = hazardSentence(ta.alert.parameters);

  if (eta.status === 'inside') {
    return (
      <Headline tone="#ff7a7f" text={`You are inside the ${event.toLowerCase()} area right now. Take shelter.`}>
        <div>
          {ta.alert.expires ? `In effect until ${fmtTime(ta.alert.expires, timezone)}.` : null}
          {eta.clearsInMinutes !== null && ` Should pass in about ${eta.clearsInMinutes} minutes.`}
        </div>
        {hazards && <div>{hazards}</div>}
      </Headline>
    );
  }

  if (eta.status === 'arriving') {
    const range = formatEtaRange(eta);
    return (
      <Headline
        tone={color}
        text={`${event} reaches ${home.label} ${range}.`}
        soften={eta.mayMiss ? 'If it holds its current track — a small shift would take it past you.' : null}
      >
        <div>
          {formatDistance(eta.distanceMiles)} away
          {motion && ` · moving ${compassWord(motion.bearingDeg)} at ${Math.round(motion.speedMph)} mph`}
          {eta.clearsInMinutes !== null && eta.minutes !== null && (
            <> · passes in about {Math.max(5, eta.clearsInMinutes - eta.minutes)} minutes</>
          )}
        </div>
        {hazards && <div>{hazards}</div>}
      </Headline>
    );
  }

  if (eta.status === 'moving-away') {
    return (
      <Headline tone="#78b7ff" text={`This ${event.toLowerCase()} is not headed your way.`}>
        <div>
          It is {formatDistance(eta.distanceMiles)} from {home.label}
          {motion && ` and moving ${compassWord(motion.bearingDeg)}`}.
        </div>
      </Headline>
    );
  }

  // Unknown. Say what we know and refuse the rest — a guessed arrival time here
  // would be the single most harmful thing this card could do.
  const why =
    eta.reason === 'stale-motion'
      ? "the storm track we have is too old to trust"
      : eta.reason === 'zero-speed'
        ? 'the storm is barely moving'
        : "this alert doesn't include a storm track";
  return (
    <Headline tone="#ffa857" text={`A ${event.toLowerCase()} is active ${formatDistance(eta.distanceMiles)} away.`}>
      <div>We can't estimate timing — {why}.</div>
      {hazards && <div>{hazards}</div>}
    </Headline>
  );
}

/**
 * Precipitation from radar, with no warning behind it. Worded more tentatively than the
 * warning answer on purpose: this vector is the mean flow of the whole scene, not the
 * track of one cell.
 *
 * Deliberately says "precipitation" and never "rain". Radar cannot tell us which it is
 * without a classification we do not have, and on a tool county EM acts on, calling an
 * ice event rain is the wrong kind of wrong.
 */
function PrecipAnswer({ eta, home }: { eta: EtaResult; home: HomePoint }) {
  if (eta.status === 'inside') {
    return (
      <Headline tone="#4fd6ef" text={`Precipitation is over ${home.label} now.`}>
        {eta.clearsInMinutes !== null && <div>Should clear in about {eta.clearsInMinutes} minutes.</div>}
      </Headline>
    );
  }
  return (
    <Headline
      tone="#4fd6ef"
      text={`Precipitation reaches ${home.label} ${formatEtaRange(eta)}.`}
      soften="No warning is in effect — this is the general drift of what radar is seeing, so treat it as a rough guide."
    >
      <div>{formatDistance(eta.distanceMiles)} away</div>
    </Headline>
  );
}

function Headline({
  tone,
  text,
  soften,
  children,
}: {
  tone: string;
  text: string;
  soften?: string | null;
  children?: React.ReactNode;
}) {
  return (
    <div style={{ display: 'flex', gap: 10 }}>
      <span style={{ width: 4, borderRadius: 2, background: tone, flexShrink: 0 }} aria-hidden="true" />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0 }}>
        <div style={{ fontSize: 15, fontWeight: 600, lineHeight: 1.35, color: '#eaf1fb' }}>{text}</div>
        {soften && <div style={{ fontSize: 13, color: '#ffc98a' }}>{soften}</div>}
        <div style={{ fontSize: 12.5, color: '#b7c6de', display: 'flex', flexDirection: 'column', gap: 3 }}>
          {children}
        </div>
      </div>
    </div>
  );
}

/**
 * Permanent, not dismissible, not a tooltip. Someone may move people on the
 * strength of the number above it.
 */
function Hedge({ source }: { source: string | null }) {
  const provenance =
    source === 'radar'
      ? 'Estimated from radar'
      : source
        ? 'From the National Weather Service warning'
        : 'No storm track available';
  return (
    <div
      style={{
        fontSize: 12,
        color: '#93a5c1',
        background: '#131d31',
        borderRadius: 6,
        padding: '7px 9px',
        lineHeight: 1.45,
      }}
    >
      <strong style={{ color: '#b7c6de' }}>{provenance}.</strong> Estimate only — storms speed up, slow down and
      turn. Follow NWS guidance and local officials.
    </div>
  );
}

function HomeChooser({
  assets,
  tenantCenter,
  tenantLabel,
  locating,
  locError,
  onUseLocation,
  onPickAsset,
  onCancel,
}: {
  assets: Asset[];
  tenantCenter?: [number, number] | null;
  tenantLabel: string;
  locating: boolean;
  locError: string | null;
  onUseLocation: () => void;
  onPickAsset: (a: Asset) => void;
  onCancel?: () => void;
}) {
  const enabled = assets.filter((a) => a.enabled);
  const suggested = nearestAsset(assets, tenantCenter);
  const [assetId, setAssetId] = useState(suggested?._id ?? '');
  const geoOk = canUseGeolocation();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ fontSize: 15, fontWeight: 600, color: '#eaf1fb' }}>Where should we check?</div>

      {enabled.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <select
            className="fld"
            value={assetId}
            onChange={(e) => setAssetId(e.target.value)}
            style={{ height: 38 }}
            aria-label="Pick a site"
          >
            {enabled.map((a) => (
              <option key={a._id} value={a._id}>
                {a.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="chip hov"
            style={{ minHeight: 38, width: '100%' }}
            onClick={() => {
              const a = enabled.find((x) => x._id === assetId);
              if (a) onPickAsset(a);
            }}
          >
            Use this site
          </button>
        </div>
      )}

      {geoOk ? (
        <button type="button" className="chip hov" style={{ minHeight: 38, width: '100%' }} onClick={onUseLocation} disabled={locating}>
          {locating ? 'Getting your location…' : 'Use my current location'}
        </button>
      ) : (
        <div style={{ fontSize: 12.5, color: '#93a5c1' }}>
          Your browser won't share your location over an insecure connection, so pick a site above.
        </div>
      )}

      {locError && <div style={{ fontSize: 12.5, color: '#ffa857' }}>{locError}</div>}
      {!enabled.length && !geoOk && (
        <div style={{ fontSize: 12.5, color: '#93a5c1' }}>
          No sites are set up yet. Add one on the Assets page, and we'll use the centre of {tenantLabel} until then.
        </div>
      )}

      {onCancel && (
        <button type="button" className="chip hov" style={{ alignSelf: 'flex-start' }} onClick={onCancel}>
          Cancel
        </button>
      )}
    </div>
  );
}
